import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { GlobalUser, AppNotification } from '../../types';
import { NotificationMenu } from '../ui/NotificationBell';
import { useAppVersionDisplay } from '../../hooks/useAppVersionDisplay';
import { KlSheet } from './modal/KlSheet';

interface MobileAppHeaderProps {
    onBack?: () => void;
    currentUser?: GlobalUser;
    onLogout?: () => void;
    notifications?: AppNotification[];
    setNotifications?: React.Dispatch<React.SetStateAction<AppNotification[]>>;
}

export const MobileAppHeader: React.FC<MobileAppHeaderProps> = ({
    onBack,
    currentUser,
    onLogout,
    notifications,
    setNotifications
}) => {
    const location = useLocation();
    const navigate = useNavigate();
    const [isNotificationOpen, setIsNotificationOpen] = useState(false);
    const [isUserModalOpen, setIsUserModalOpen] = useState(false);
    const { fullVersionString } = useAppVersionDisplay();
    const userInitial = (currentUser?.name || currentUser?.email || 'U').charAt(0).toUpperCase();

    const handleGoHome = () => {
        if (onBack) {
            onBack();
        } else {
            window.dispatchEvent(new CustomEvent('kl-go-home'));
        }
    };

    const handleBack = () => {
        if (location.pathname !== '/' && location.pathname !== '') {
            if (window.history.length > 1) {
                navigate(-1);
            } else {
                navigate('/');
            }
        } else {
            handleGoHome();
        }
    };

    const unreadCount = (notifications || []).filter(n => {
        if (n.isRead) return false;
        if (!currentUser) return true;
        if (n.userId === currentUser.id || n.userId === currentUser.email) return true;
        if (n.userId === 'all') {
            if (currentUser.globalRole === 'Administrador') return true;
            if (n.projectId) return currentUser.assignedProjects.some(ap => ap.projectId === n.projectId);
            return true;
        }
        return false;
    }).length;

    return (
        <header
            style={{
                height: 'calc(48px + var(--kl-safe-top, env(safe-area-inset-top, 0px)))',
                paddingTop: 'var(--kl-safe-top, env(safe-area-inset-top, 0px))',
                background: '#ffffff',
                display: 'flex',
                alignItems: 'center',
                paddingLeft: '6px',
                paddingRight: '12px',
                paddingBottom: 0,
                gap: '6px',
                boxSizing: 'border-box',
                position: 'sticky',
                top: 0,
                zIndex: 30,
                borderBottom: '1px solid var(--kl-border-subtle, #F1F5F9)'
            }}
        >
            {/* 1. Botón Home a la izquierda del logo (22px, área táctil 40x40) */}
            <button
                type="button"
                onClick={handleGoHome}
                aria-label="Seleccionar obra / Bienvenida"
                title="Volver a selección de obra"
                style={{
                    border: 0,
                    background: 'none',
                    color: 'var(--kl-text-2, #334155)',
                    padding: 0,
                    width: '40px',
                    height: '40px',
                    minWidth: '40px',
                    minHeight: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer',
                    borderRadius: '8px',
                    flexShrink: 0
                }}
            >
                <svg
                    style={{ width: '22px', height: '22px', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}
                    viewBox="0 0 24 24"
                >
                    <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1h-5v-6h-6v6H4a1 1 0 01-1-1V9.5z" />
                </svg>
            </button>

            {/* 2. Logo Real de la App */}
            <div
                style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '7px',
                    overflow: 'hidden',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    background: '#ffffff',
                    border: '1px solid var(--kl-border, #E2E8F0)'
                }}
            >
                <img
                    src="/assets/play_store_icon.png"
                    alt="K-LyDapp"
                    style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'contain'
                    }}
                />
            </div>

            {/* 3. Título K-LyDapp */}
            <b
                style={{
                    flex: 1,
                    fontSize: '15px',
                    fontWeight: 700,
                    color: 'var(--kl-text, #0F172A)',
                    letterSpacing: '-0.01em'
                }}
            >
                K-LyDapp
            </b>

            {/* 4. Botón Notificaciones */}
            <div style={{ position: 'relative' }}>
                <button
                    type="button"
                    onClick={() => setIsNotificationOpen(!isNotificationOpen)}
                    aria-label="Notificaciones"
                    style={{
                        border: 0,
                        background: 'none',
                        color: 'var(--kl-text-2, #334155)',
                        padding: 0,
                        width: '32px',
                        height: '32px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'pointer',
                        position: 'relative'
                    }}
                >
                    <svg
                        style={{ width: '22px', height: '22px', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}
                        viewBox="0 0 24 24"
                    >
                        <path d="M6 8a6 6 0 0112 0c0 7 3 9 3 9H3s3-2 3-9M10 21h4" />
                    </svg>
                    {(unreadCount > 0 || !notifications || notifications.length === 0) && (
                        <span
                            data-unread-dot="true"
                            style={{
                                position: 'absolute',
                                top: '3px',
                                right: '3px',
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                backgroundColor: 'var(--kl-danger, #DC2626)',
                                border: '1.5px solid #ffffff',
                                boxShadow: '0 0 2px rgba(220, 38, 38, 0.4)',
                                pointerEvents: 'none'
                            }}
                        />
                    )}
                </button>
            </div>

            {/* Hoja Inferior KlSheet de Notificaciones */}
            {isNotificationOpen && notifications && setNotifications && (
                <KlSheet
                    open={isNotificationOpen}
                    onClose={() => setIsNotificationOpen(false)}
                    title="Notificaciones"
                    top={56}
                >
                    <div style={{ margin: '-14px' }}>
                        <NotificationMenu
                            notifications={notifications}
                            setNotifications={setNotifications}
                            currentUser={currentUser}
                            onClose={() => setIsNotificationOpen(false)}
                        />
                    </div>
                </KlSheet>
            )}

            {/* 5. Avatar del usuario (interactivo para ver versión y datos) */}
            <button
                type="button"
                onClick={() => setIsUserModalOpen(true)}
                title={currentUser?.name || currentUser?.email || 'Usuario'}
                aria-label="Perfil y versión de la aplicación"
                style={{
                    width: '28px',
                    height: '28px',
                    borderRadius: '50%',
                    background: 'var(--kl-primary-100, #DBEAFE)',
                    color: 'var(--kl-primary-dark, #1D4ED8)',
                    fontSize: '12px',
                    fontWeight: 700,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                    border: '1.5px solid var(--kl-primary-300, #93C5FD)',
                    cursor: 'pointer',
                    padding: 0
                }}
            >
                {userInitial}
            </button>

            {/* Hoja Inferior KlSheet de Información de Usuario y Versión */}
            <KlSheet
                open={isUserModalOpen}
                onClose={() => setIsUserModalOpen(false)}
                title="Mi Perfil"
                footer={
                    <button
                        type="button"
                        onClick={() => setIsUserModalOpen(false)}
                        className="w-full h-11 rounded-[12px] font-bold text-sm bg-white dark:bg-slate-800 border border-[#CBD5E1] dark:border-slate-700 text-[#334155] dark:text-slate-200 active:scale-[0.98] transition-transform"
                    >
                        Cerrar
                    </button>
                }
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                    <div
                        style={{
                            width: '44px',
                            height: '44px',
                            borderRadius: '50%',
                            background: 'var(--kl-primary-100, #DBEAFE)',
                            color: 'var(--kl-primary-dark, #1D4ED8)',
                            fontSize: '18px',
                            fontWeight: 700,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            flexShrink: 0
                        }}
                    >
                        {userInitial}
                    </div>
                    <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ fontSize: '15px', fontWeight: 700, color: '#0F172A', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {currentUser?.name || 'Usuario'}
                        </div>
                        <div style={{ fontSize: '12px', color: '#64748B', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {currentUser?.email || ''}
                        </div>
                    </div>
                </div>

                <div style={{ background: '#F8FAFC', borderRadius: '12px', padding: '12px', border: '1px solid #E2E8F0' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Rol en Plataforma</span>
                        <span style={{
                            fontSize: '11px',
                            fontWeight: 700,
                            padding: '2px 8px',
                            borderRadius: '6px',
                            background: currentUser?.globalRole === 'Administrador' ? '#EEF2FF' : '#F1F5F9',
                            color: currentUser?.globalRole === 'Administrador' ? '#4F46E5' : '#475569'
                        }}>
                            {currentUser?.globalRole === 'Administrador' ? 'Administrador' : 'Usuario'}
                        </span>
                    </div>
                    {currentUser?.companyName && (
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                            <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Empresa</span>
                            <span style={{ fontSize: '12px', fontWeight: 600, color: '#1E293B' }}>{currentUser.companyName}</span>
                        </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <span style={{ fontSize: '11px', fontWeight: 600, color: '#64748B', textTransform: 'uppercase' }}>Versión App</span>
                        <span style={{ fontSize: '12px', fontWeight: 700, color: '#2563EB' }}>{fullVersionString}</span>
                    </div>
                </div>
            </KlSheet>

            {/* 6. Botón Salir */}
            <button
                type="button"
                onClick={onLogout}
                aria-label="Salir"
                style={{
                    border: 0,
                    background: 'none',
                    color: 'var(--kl-text-2, #334155)',
                    padding: 0,
                    width: '32px',
                    height: '32px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: 'pointer'
                }}
            >
                <svg
                    style={{ width: '22px', height: '22px', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' }}
                    viewBox="0 0 24 24"
                >
                    <path d="M9 21H5V3h4M16 17l5-5-5-5M21 12H9" />
                </svg>
            </button>
        </header>
    );
};
